import React, { useMemo, useState } from 'react'
import { Checkbox, Spin, Empty } from 'antd'
import dayjs from 'dayjs'
import icon_play from '../../../../../images/icon_play.png'
import styles from './index.module.css'

interface Photo {
  id: string
  smallURL: string
  mediaType: 0 | 1
  time: number
  addr?: string
}

interface PhotoGridProps {
  photos: Photo[]
  selectedIds: string[]
  loading: boolean
  onToggleSelect: (id: string) => void
  onClickPhoto: (index: number) => void
  disabled?: boolean
}

interface PhotoCardProps {
  photo: Photo
  index: number
  isSelected: boolean
  readyToCheck: boolean
  disabled: boolean
  onToggleSelect: (id: string) => void
  onClickPhoto: (index: number) => void
}

const PhotoCard: React.FC<PhotoCardProps> = ({
  photo,
  index,
  isSelected,
  readyToCheck,
  disabled,
  onToggleSelect,
  onClickPhoto
}) => {
  const [hovered, setHovered] = useState(false)
  const showGrayBg = hovered || isSelected // 灰底：仅 hover 或自身被选中
  const showCheckbox = showGrayBg || readyToCheck // checkbox：批量模式下也显示

  return (
    <div
      className={styles.card}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onClickPhoto(index)}
    >
      {/* 灰色背景层（z-index:1，仅背景色） */}
      <div
        className={styles.checkboxWrapper}
        style={{
          background: showGrayBg ? 'rgba(247,248,249,1)' : 'transparent'
        }}
      />

      {/* Checkbox（z-index:3，点击切换选中，阻止冒泡防止触发预览） */}
      {showCheckbox && (
        <Checkbox
          className={styles.checkBox}
          checked={isSelected}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation()
            if (!disabled) onToggleSelect(photo.id)
          }}
        />
      )}

      {/* 照片内容（图片 + 文字信息） */}
      <div className={styles.photoContent}>
        <div
          className={styles.photo}
          style={{ backgroundImage: `url(${photo.smallURL})` }}
        >
          {photo.mediaType === 1 && (
            <img className={styles.playIcon} src={icon_play} alt="" />
          )}
        </div>
        <p className={styles.info}>
          {dayjs(photo.time).format('M月D日 HH:mm')}
        </p>
        {photo.addr && (
          <p className={`${styles.info} ${styles.addr}`}>{photo.addr}</p>
        )}
      </div>
    </div>
  )
}

const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  selectedIds,
  loading,
  onToggleSelect,
  onClickPhoto,
  disabled = false
}) => {
  const readyToCheck = selectedIds.length > 0
  // O(1) 查找：避免在 photos.map 中调用 selectedIds.includes 退化为 O(n²)
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    )
  }

  if (photos.length === 0) {
    return <Empty description="暂无照片" style={{ marginTop: 80 }} />
  }

  return (
    <div className={styles.grid}>
      {photos.map((photo, index) => (
        <PhotoCard
          key={photo.id}
          photo={photo}
          index={index}
          isSelected={selectedIdSet.has(photo.id)}
          readyToCheck={readyToCheck}
          disabled={disabled}
          onToggleSelect={onToggleSelect}
          onClickPhoto={onClickPhoto}
        />
      ))}
    </div>
  )
}

export default PhotoGrid
